import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, ChevronLeft, ChevronRight, FileText, Download, Lock } from "lucide-react";

const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const thisMonday = () => {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
};

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const downloadCsv = async (url, filename) => {
  const token = localStorage.getItem("token");
  const BASE = process.env.REACT_APP_API_URL || "";
  const res = await fetch(`${BASE}/api${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const StatusBadge = ({ status }) => {
  const map = {
    approved: "text-[#00F5D4] bg-[#00F5D4]/10",
    rejected: "text-[#FF007A] bg-[#FF007A]/10",
    pending: "text-[#F59E0B] bg-[#F59E0B]/10",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 ${map[status] || map.pending}`}>
      {status || "pending"}
    </span>
  );
};

const TimeTracking = () => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(thisMonday());
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ project_id: "", hours_spent: "", description: "", date_logged: "", activity_type_id: "" });
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ project_id: "", due_date: "", currency: "GBP", issue_date: new Date().toISOString().slice(0, 10) });
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);

  const weekEnd = addDays(weekStart, 6);
  const loadReqRef = useRef(0);

  const load = useCallback(async () => {
    // Bumping this invalidates any still-in-flight load, so navigating
    // weeks quickly can't have a slower earlier week's response overwrite
    // the logs for the week currently shown.
    const reqId = ++loadReqRef.current;
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/projects?limit=200"),
        api.get("/activity-types").catch(() => ({ data: [] })),
      ]);
      if (reqId !== loadReqRef.current) return;
      setProjects(p.data);
      setActivityTypes(a.data || []);
      // Fetch time logs for all projects in the week
      const allLogs = await Promise.all(
        p.data.map((proj) =>
          api.get(`/projects/${proj.project_id}/time-logs?start_date=${weekStart}&end_date=${weekEnd}`)
            .then((r) => r.data.map((l) => ({ ...l, project_name: proj.name })))
            .catch(() => [])
        )
      );
      if (reqId !== loadReqRef.current) return;
      setLogs(allLogs.flat().sort((a, b) => (b.date_logged || "").localeCompare(a.date_logged || "")));
    } catch { if (reqId === loadReqRef.current) toast.error("Failed to load time logs"); }
    finally { if (reqId === loadReqRef.current) setLoading(false); }
  }, [weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);

  const totalHours = logs.reduce((s, l) => s + (l.hours_spent || 0), 0);

  // Group logs by date
  const byDate = {};
  for (const l of logs) {
    const d = l.date_logged || "unknown";
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(l);
  }

  const save = async () => {
    if (!form.project_id || !form.hours_spent || !form.date_logged) return toast.error("Fill project, hours and date");
    setBusy(true);
    try {
      await api.post(`/projects/${form.project_id}/time-logs`, {
        hours_spent: Number(form.hours_spent),
        description: form.description || "",
        date_logged: form.date_logged,
        activity_type_id: form.activity_type_id || undefined,
      });
      toast.success("Time logged");
      setAdding(false);
      setForm({ project_id: "", hours_spent: "", description: "", date_logged: weekStart, activity_type_id: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const saveEdit = async (log) => {
    setBusy(true);
    try {
      await api.patch(`/time-logs/${log.log_id}`, {
        hours_spent: Number(editForm.hours_spent) || log.hours_spent,
        description: editForm.description ?? log.description,
        date_logged: editForm.date_logged || log.date_logged,
      });
      toast.success("Updated");
      setEditId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const remove = async (log) => {
    try {
      await api.delete(`/time-logs/${log.log_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const invoiceableLogs = logs.filter((l) => !l.invoiced_id);

  const toggleAll = () =>
    setSelected((prev) => prev.size === invoiceableLogs.length ? new Set() : new Set(invoiceableLogs.map((l) => l.log_id)));

  const createInvoice = async () => {
    if (!invoiceForm.project_id) return toast.error("Select a project");
    if (!invoiceForm.due_date) return toast.error("Enter due date");
    const selectedLogs = logs.filter((l) => selected.has(l.log_id));
    const otherProject = selectedLogs.find((l) => l.project_id !== invoiceForm.project_id);
    if (otherProject) return toast.error("Selected entries span more than one project — pick the matching project or narrow your selection");
    setInvoiceBusy(true);
    try {
      const { data } = await api.post("/invoices/from-time-logs", {
        project_id: invoiceForm.project_id,
        log_ids: selectedLogs.map((l) => l.log_id),
        issue_date: invoiceForm.issue_date,
        due_date: invoiceForm.due_date,
        currency: invoiceForm.currency,
      });
      toast.success("Invoice created from time logs");
      setInvoiceOpen(false);
      setSelected(new Set());
      if (data?.invoice_id) navigate(`/invoices/${data.invoice_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setInvoiceBusy(false);
    }
  };

  // Weekly bar (hours per day)
  const dayTotals = DAYS.map((_, i) => {
    const d = addDays(weekStart, i);
    return logs.filter((l) => l.date_logged === d).reduce((s, l) => s + (l.hours_spent || 0), 0);
  });
  const maxDay = Math.max(...dayTotals, 8);

  return (
    <div>
      <PageHeader
        eyebrow="People"
        title="Time tracking"
        description="Log and review hours across all projects for the selected week."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => downloadCsv("/time-logs/export", "time-logs.csv")}
              className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              onClick={() => { setAdding(true); setForm({ project_id: "", hours_spent: "", description: "", date_logged: weekStart }); }}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480]"
              data-testid="add-time-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Log time
            </Button>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-6 space-y-6">
        {/* Week nav */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setWeekStart((w) => addDays(w, -7))} className="rounded-none h-8 w-8 p-0 border border-[#1F2937] text-[#6B7280] hover:text-white">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#9CA3AF] font-mono">{fmtDate(weekStart)} — {fmtDate(weekEnd)}</span>
          <Button variant="ghost" onClick={() => setWeekStart((w) => addDays(w, 7))} className="rounded-none h-8 w-8 p-0 border border-[#1F2937] text-[#6B7280] hover:text-white">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="ml-auto text-sm text-[#9CA3AF]">
            Total: <span className="font-mono text-white font-bold">{totalHours.toFixed(1)}h</span>
          </div>
        </div>

        {/* Day bars */}
        <div className="border border-[#1F2937] bg-[#0D1117] p-4">
          <div className="flex items-end gap-2 h-20">
            {DAYS.map((day, i) => {
              const h = dayTotals[i];
              const pct = maxDay > 0 ? (h / maxDay) * 100 : 0;
              const colour = h === 0 ? "#1F2937" : h >= 8 ? "#FF007A" : h >= 6 ? "#F59E0B" : "#00F5D4";
              return (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end justify-center" style={{ height: 56 }}>
                    <div style={{ height: `${Math.max(pct, h > 0 ? 8 : 0)}%`, background: colour, width: "100%", transition: "height 0.3s ease" }} className="rounded-sm" />
                  </div>
                  <div className="text-[10px] text-[#6B7280]">{day}</div>
                  {h > 0 && <div className="text-[10px] font-mono text-[#9CA3AF]">{h}h</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Add form */}
        {adding && (
          <div className="border border-[#1F2937] bg-[#0D1117] p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">New time entry</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Project</div>
                <select
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                  data-testid="time-project-select"
                >
                  <option value="">— project —</option>
                  {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Date</div>
                <input type="date" value={form.date_logged} onChange={(e) => setForm({ ...form, date_logged: e.target.value })}
                  className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm" data-testid="time-date-input" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Hours</div>
                <input type="number" min="0.25" max="24" step="0.25" value={form.hours_spent} onChange={(e) => setForm({ ...form, hours_spent: e.target.value })}
                  placeholder="e.g. 3.5" className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm" data-testid="time-hours-input" />
              </div>
              {activityTypes.length > 0 && (
                <div className="md:col-span-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Activity type (sets billing rate)</div>
                  <select
                    value={form.activity_type_id}
                    onChange={(e) => setForm({ ...form, activity_type_id: e.target.value })}
                    className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                  >
                    <option value="">— none —</option>
                    {activityTypes.map((a) => <option key={a.activity_type_id} value={a.activity_type_id}>{a.name} ({a.currency} {a.default_rate}/{a.unit})</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Description</div>
              <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What did you work on?"
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-3 text-sm" data-testid="time-desc-input" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={save} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="time-save-btn">
                {busy ? "Saving…" : "Save"}
              </Button>
              <Button variant="ghost" onClick={() => setAdding(false)} className="rounded-none text-[#6B7280]">Cancel</Button>
            </div>
          </div>
        )}

        {/* Selection action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-4 border border-[#00F5D4]/30 bg-[#00F5D4]/5 px-4 py-3">
            <span className="text-sm text-[#00F5D4] font-mono">{selected.size} entr{selected.size === 1 ? "y" : "ies"} selected</span>
            <Button
              onClick={() => setInvoiceOpen(true)}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-8 px-3 text-xs"
            >
              <FileText className="w-3.5 h-3.5 mr-1" /> Create invoice
            </Button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-[#6B7280] hover:text-white ml-auto">Clear</button>
          </div>
        )}

        {/* Log table */}
        <div className="border border-[#1F2937]">
          <div className="px-4 py-3 border-b border-[#1F2937] bg-[#111827] text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">
            Entries this week
          </div>
          {loading ? (
            <div className="py-12 text-center text-sm text-[#6B7280]">Loading…</div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#6B7280]" data-testid="time-empty">
              No time logged this week.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1F2937]">
                    <th className="px-4 py-2.5 bg-[#0D1117] w-8">
                      <input type="checkbox" checked={selected.size === invoiceableLogs.length && invoiceableLogs.length > 0} onChange={toggleAll}
                        className="accent-[#00F5D4] cursor-pointer" />
                    </th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Date</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Project</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Description</th>
                    <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Hours</th>
                    <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Status</th>
                    <th className="px-4 py-2.5 bg-[#0D1117] w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.log_id} className={`border-b border-[#1F2937] hover:bg-[#111827] ${selected.has(log.log_id) ? "bg-[#00F5D4]/5" : "bg-[#0D1117]"}`} data-testid={`time-row-${log.log_id}`}>
                      <td className="px-4 py-2.5">
                        <input type="checkbox" checked={selected.has(log.log_id)} onChange={() => toggleSelect(log.log_id)}
                          disabled={!!log.invoiced_id} title={log.invoiced_id ? "Already on an invoice" : undefined}
                          className="accent-[#00F5D4] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#9CA3AF] font-mono whitespace-nowrap">
                        {editId === log.log_id ? (
                          <input type="date" value={editForm.date_logged || log.date_logged} onChange={(e) => setEditForm({ ...editForm, date_logged: e.target.value })}
                            className="w-32 h-7 rounded-none border border-[#374151] bg-[#030712] text-white px-1 text-xs" />
                        ) : fmtDate(log.date_logged || "")}
                      </td>
                      <td className="px-4 py-2.5 text-white truncate max-w-[140px]">{log.project_name || "—"}</td>
                      <td className="px-4 py-2.5 text-[#9CA3AF] max-w-[200px]">
                        {editId === log.log_id ? (
                          <input value={editForm.description ?? log.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="w-full h-7 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-xs" />
                        ) : (
                          <div>
                            <div className="truncate">{log.description || "—"}</div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {log.activity_type_name && (
                                <span className="text-[10px] text-[#00F5D4] bg-[#00F5D4]/10 px-1.5 py-0.5 truncate">{log.activity_type_name}</span>
                              )}
                              {log.invoiced_id && (
                                <span className="text-[10px] text-[#6B7280] bg-[#1F2937] px-1.5 py-0.5">Invoiced</span>
                              )}
                            </div>
                            {log.approval_status === "rejected" && log.approval_note && (
                              <div className="text-[10px] text-[#FF007A] truncate mt-0.5">
                                Rejected: {log.approval_note}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        {editId === log.log_id ? (
                          <input type="number" min="0.25" max="24" step="0.25" value={editForm.hours_spent ?? log.hours_spent}
                            onChange={(e) => setEditForm({ ...editForm, hours_spent: e.target.value })}
                            className="w-16 h-7 rounded-none border border-[#374151] bg-[#030712] text-white px-1 text-xs text-right" />
                        ) : <span className="text-white">{log.hours_spent}h</span>}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <StatusBadge status={log.approval_status} />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {log.approval_status === "approved" || log.invoiced_id ? (
                            <span className="flex items-center gap-1 text-[#374151]" title={log.invoiced_id ? "Locked — already on an invoice." : "Locked — approved. Ask a manager to reopen it before editing."}>
                              <Lock className="w-3.5 h-3.5" />
                            </span>
                          ) : editId === log.log_id ? (
                            <>
                              <button onClick={() => saveEdit(log)} disabled={busy} className="text-[#00F5D4] hover:text-white" title="Save">
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setEditId(null)} className="text-[#6B7280] hover:text-white" title="Cancel">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => { setEditId(log.log_id); setEditForm({}); }} className="text-[#374151] hover:text-[#9CA3AF]" title="Edit"
                                data-testid={`edit-time-${log.log_id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => remove(log)} className="text-[#374151] hover:text-[#FF007A]" title="Delete"
                                data-testid={`delete-time-${log.log_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#374151]">
                    <td colSpan={4} className="px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">Week total</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold text-white">{totalHours.toFixed(1)}h</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Time → Invoice dialog */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold tracking-tight">Create invoice from time logs</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-[#6B7280]">
              {selected.size} entr{selected.size === 1 ? "y" : "ies"} selected · {logs.filter((l) => selected.has(l.log_id)).reduce((s, l) => s + (l.hours_spent || 0), 0).toFixed(1)}h total
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Project *</div>
              <select
                value={invoiceForm.project_id}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, project_id: e.target.value })}
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-3 text-sm"
              >
                <option value="">Select project…</option>
                {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Issue date</div>
                <input type="date" value={invoiceForm.issue_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, issue_date: e.target.value })}
                  className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-3 text-sm" />
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Due date *</div>
                <input type="date" value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                  className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-3 text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Currency</div>
              <select value={invoiceForm.currency}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, currency: e.target.value })}
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm">
                <option value="GBP">GBP £</option>
                <option value="USD">USD $</option>
                <option value="EUR">EUR €</option>
              </select>
            </div>
            {(() => {
              const selectedLogs = logs.filter((l) => selected.has(l.log_id));
              const groups = {};
              for (const l of selectedLogs) {
                const rate = l.hourly_billing_rate_at_time_of_log || 0;
                const key = `${l.activity_type_id || "none"}::${rate}`;
                if (!groups[key]) groups[key] = { label: l.activity_type_name || "General time", rate, hours: 0 };
                groups[key].hours += l.hours_spent || 0;
              }
              const rows = Object.values(groups);
              if (rows.length === 0) return null;
              return (
                <div className="border border-[#1F2937] divide-y divide-[#1F2937]">
                  <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Line items preview</div>
                  {rows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                      <span className="text-white">{r.label}</span>
                      <span className="text-[#9CA3AF] font-mono">{r.hours.toFixed(2)}h @ {r.rate.toFixed(2)} = {(r.hours * r.rate).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setInvoiceOpen(false)} className="rounded-none text-[#6B7280]">Cancel</Button>
            <Button onClick={createInvoice} disabled={invoiceBusy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">
              {invoiceBusy ? "Creating…" : "Create invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTracking;
